import { useState, useEffect, useRef } from 'react';
import {
  Dialog, IconButton, InputAdornment, TextField, Divider,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkRemove from '@mui/icons-material/BookmarkRemove';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { useSnackbar } from 'notistack';
import { useRouter } from 'next/router';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { useUserLists } from 'lib/contexts/userListsContext';
import { List, ListOrProjectWithStages } from 'lib/types';
import { Project } from 'lib/types/projects';
import { userIsProOrAdmin, userIsPaidTeamMember } from 'lib/helpers';
import { toSlug } from 'lib/utils/filterUtils';
import { useUpgradeModal } from 'components/UpgradeModal';
import { AddEditList } from 'components/AddEditList';
import { FAVORITES_LIST_NAME } from 'lib/constants';
import Link from 'next/link';
import { MobileTooltip } from 'components/MobileTooltip';

const getTriggerColorClasses = (trigger?: string) => {
  if (!trigger) return 'bg-paleyellow text-darkorange';

  if (trigger === 'selected') {
    return 'bg-palegreen text-green border border-green';
  }

  if (trigger === 'hidden') {
    return 'bg-palered text-red border border-red';
  }

  // Default state (yellow)
  return 'bg-paleyellow text-darkorange border border-orange';
};

interface Props {
  /** If provided, only show lists and projects that are part of the team */
  teamId?: number;
  userId: number;
  /** Lists that the user is already saved to */
  listsWithStages?: ListOrProjectWithStages[];
  /** Projects that the user is already saved to */
  projectsWithStages?: ListOrProjectWithStages[];
  onAddToList: (listId: number) => void;
  onRemoveFromList: (listId: number) => void;
  onAddToProject: (projectId: number) => void;
  onRemoveFromProject: (projectId: number) => void;
  onCancel: () => void;
}

export const SaveToModal = ({
  teamId,
  userId,
  listsWithStages = [],
  projectsWithStages = [],
  onAddToList,
  onRemoveFromList,
  onAddToProject,
  onRemoveFromProject,
  onCancel,
}: Props) => {
  const userToSave = userId;
  const currentUser = useCurrentUser();
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const { showUpgradeModal } = useUpgradeModal();
  const router = useRouter();

  const [isSendingData, setIsSendingData] = useState(false);
  const { lists, setLists } = useUserLists();
  const [projects, setProjects] = useState<Project[]>([]);
  const [adding, setAdding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const hasFetchedProjects = useRef(false);

  // Add stage to filterLists if available
  const filterLists = (allLists: List[] | undefined) => {
    if (!currentUser?.id) return [];
    if (!allLists?.length) return [];
    if (userIsProOrAdmin(currentUser)) {
      if (teamId) {
        return allLists.filter((l) => l.teamId === teamId);
      }
      return allLists;
    }

    const allowedLists: List[] = [];
    allLists.forEach((l) => {
      if (teamId && l.teamId !== teamId) return;
      // All users can see their personal lists
      if (!l.teamId) {
        allowedLists.push(l);
      }
      // Any active team subscription allows job lists
      if (l.jobId) {
        allowedLists.push(l);
      }
    });

    return allowedLists;
  };

  const filteredLists = filterLists(lists);

  // Fetch projects
  useEffect(() => {
    const getProjects = async () => {
      const p = await API.getProjects(teamId);
      setProjects(p);
    };

    if (currentUser && API && !hasFetchedProjects.current) {
      hasFetchedProjects.current = true;
      getProjects();
    }
  }, [API, currentUser, teamId]);

  // Filter items by search
  const filterItemsBySearch = (items: any[], searchText: string) => {
    if (!searchText) return items;
    const query = searchText.toLowerCase();
    return items.filter((item) => {
      if (item.name) return item.name.toLowerCase().includes(query);
      if (item.teamName) return item.teamName.toLowerCase().includes(query);
      return false;
    });
  };

  // Separate already saved items from available items
  const isListAlreadySaved = (l: List) => l.name !== FAVORITES_LIST_NAME
    && listsWithStages?.some((list) => list.id === l.id);
  const isListAvailable = (l: List) => l.name !== FAVORITES_LIST_NAME
    && !listsWithStages?.some((list) => list.id === l.id);

  const alreadySavedLists = filteredLists?.filter(isListAlreadySaved) || [];
  const availableLists = filteredLists?.filter(isListAvailable) || [];

  const alreadySavedProjects = projects?.filter(
    (p) => projectsWithStages?.some((project) => project.id === p.id),
  ) || [];
  const availableProjects = projects?.filter(
    (p) => !projectsWithStages?.some((project) => project.id === p.id),
  ) || [];

  const filteredAlreadySavedLists = filterItemsBySearch(alreadySavedLists, searchQuery);
  const filteredAvailableLists = filterItemsBySearch(availableLists, searchQuery);
  const filteredAlreadySavedProjects = filterItemsBySearch(alreadySavedProjects, searchQuery);
  const filteredAvailableProjects = filterItemsBySearch(availableProjects, searchQuery)
    .sort((a, b) => a.name.localeCompare(b.name));

  const addToList = async (list: List) => {
    try {
      setIsSendingData(true);
      await API.addUserToList(list.id, userToSave);
      setIsSendingData(false);
      onAddToList(list.id);
      const action = () => (
        <button type="button" className="btn">
          <Link href={`/creators?list=${toSlug(list.name, list.publicId)}`}>
            View list
          </Link>
        </button>
      );
      enqueueSnackbar(`Added creator to ${list.name}!`, {
        variant: 'success',
        action,
        autoHideDuration: 9000,
      });
    } catch (error) {
      setIsSendingData(false);
      enqueueSnackbar(`Error adding creator to list: ${error}`, {
        variant: 'error',
      });
    }
  };

  const removeFromList = async (list: List) => {
    try {
      setIsSendingData(true);
      await API.removeUserFromList(list.id, userToSave);
      setIsSendingData(false);
      onRemoveFromList(list.id);
      enqueueSnackbar(`Removed creator from ${list.name}`, {
        variant: 'success',
      });
    } catch (err) {
      setIsSendingData(false);
      enqueueSnackbar(`Error removing creator from list: ${err}`, {
        variant: 'error',
      });
    }
  };

  const addToProject = async (project: Project) => {
    try {
      setIsSendingData(true);
      await API.addCollaboratorToProject(project.id, {
        userId: userToSave,
        reasonToBelieve: '',
        projections: {},
      });
      setIsSendingData(false);
      onAddToProject(project.id);
      enqueueSnackbar(`Added creator to ${project.name}!`, {
        variant: 'success',
      });
    } catch (error) {
      setIsSendingData(false);
      enqueueSnackbar(`Error adding creator to project: ${error}`, {
        variant: 'error',
      });
    }
  };

  const removeFromProject = async (project: Project) => {
    try {
      setIsSendingData(true);
      await API.deleteProjectCollaborator(project.id, userToSave);
      setIsSendingData(false);
      onRemoveFromProject(project.id);
      enqueueSnackbar(`Removed creator from ${project.name}`, {
        variant: 'success',
      });
    } catch (err) {
      setIsSendingData(false);
      enqueueSnackbar(`Error removing creator from project: ${err}`, {
        variant: 'error',
      });
    }
  };

  const saveToList = async (list: List, added: boolean) => {
    if (!list.id) return;

    if (list.name !== FAVORITES_LIST_NAME
      && !userIsProOrAdmin(currentUser)
      && !userIsPaidTeamMember(currentUser)) {
      showUpgradeModal('Upgrade your account to add to the list');
      return;
    }

    if (!added) {
      addToList(list);
    } else {
      removeFromList(list);
    }
  };

  const saveToProject = async (project: Project, added: boolean) => {
    if (!project.id) return;

    if (!userIsProOrAdmin(currentUser) && !userIsPaidTeamMember(currentUser)) {
      showUpgradeModal('Upgrade your account to add to projects');
      return;
    }

    if (!added) {
      addToProject(project);
    } else {
      removeFromProject(project);
    }
  };

  const openAddList = () => {
    if (!userIsProOrAdmin(currentUser) && !userIsPaidTeamMember(currentUser)) {
      showUpgradeModal('Upgrade your account to create new lists');
      return;
    }
    setAdding(true);
  };

  const openAddProject = () => {
    if (!userIsProOrAdmin(currentUser) && !userIsPaidTeamMember(currentUser)) {
      showUpgradeModal('Upgrade your account to create new projects');
      return;
    }
    router.push('/projects');
  };

  const addList = (list: List) => {
    setLists([
      ...(lists || []),
      list,
    ]);
  };

  const favoritesList = filteredLists?.filter((l) => l.name === FAVORITES_LIST_NAME)[0];

  return (
    <Dialog
      open
      onClose={onCancel}
      maxWidth="md"
      fullWidth
    >
      <div className="absolute right-2 top-2 text-orange">
        <IconButton onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className="p-4">
        {!adding ? (
          <>
            <h1 className="text-lg text-orange font-bold mb-4">
              Save
              {' '}
              Creator
              {' '}
              to Lists & Projects
            </h1>

            {/* Search Bar */}
            <div className="p-2 sticky top-0 bg-white z-10">
              <TextField
                size="small"
                fullWidth
                placeholder="Search lists and projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                InputProps={{
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSearchQuery('');
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            {/* Favorites List */}
            {/* Already Saved Lists Section */}
            {filteredAlreadySavedLists.length > 0 && (
              <div className="mb-4">
                <div className="px-3 py-1 text-sm font-semibold text-gray-500 mb-2">ALREADY SAVED TO LISTS</div>
                <div className="space-y-1">
                  {filteredAlreadySavedLists.map((list) => {
                    const listWithStage = listsWithStages?.find((item) => item.id === list.id);
                    return (
                      <div className="flex">
                        <button
                          key={list.id}
                          type="button"
                          disabled={isSendingData}
                          onClick={() => saveToList(list, true)}
                          className="flex items-start justify-start p-2 hover:bg-gray-100 cursor-pointer w-full rounded"
                        >
                          <div className="flex items-start">
                            <CheckIcon color="success" className="mr-2 mt-0.5" />
                            <div className="flex flex-col">
                              <span className="text-left">{list.name}</span>
                              {list.team?.name && (
                                <span className="text-left text-sm text-gray-500">{list.team.name}</span>
                              )}
                            </div>
                          </div>
                          {listWithStage?.stage && (
                            <span className={`ml-4 text-xs px-1.5 py-0.5 rounded font-medium ${getTriggerColorClasses(listWithStage.trigger)}`}>
                              {listWithStage.stage}
                            </span>
                          )}
                          <BookmarkRemove fontSize="small" color="inherit" className="text-gold ml-auto mt-0.5" />
                        </button>
                        <MobileTooltip title="View" placement="right-start">
                          <Link href={`/creators?list=${toSlug(list.name, list.publicId)}`} target="_blank" rel="noopener noreferrer">
                            <button
                              type="button"
                              className="flex py-2 align-top cursor-pointer px-2 hover:bg-gray-100 rounded"
                            >
                              <OpenInNew fontSize="small" color="primary" />
                            </button>
                          </Link>
                        </MobileTooltip>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Already Saved Projects Section */}
            {filteredAlreadySavedProjects.length > 0 && (
              <div className="mb-4">
                <div className="px-3 py-1 text-sm font-semibold text-gray-500 mb-2">ALREADY SAVED TO PROJECTS</div>
                <div className="space-y-1">
                  {filteredAlreadySavedProjects.map((project) => {
                    const projectWithStage = projectsWithStages?.find(
                      (item) => item.id === project.id,
                    );
                    return (
                      <div className="flex">
                        <button
                          key={project.id}
                          type="button"
                          disabled={isSendingData}
                          onClick={() => saveToProject(project, true)}
                          className="flex items-start justify-start p-2 hover:bg-gray-100 cursor-pointer w-full rounded"
                        >
                          <div className="flex items-start">
                            <CheckIcon color="success" className="mr-2 mt-0.5" />
                            <div className="flex flex-col">
                              <span className="text-left">{project.name}</span>
                              {project.team?.name && (
                                <span className="text-left text-sm text-gray-500">{project.team.name}</span>
                              )}
                            </div>
                          </div>
                          {projectWithStage?.stage && (
                            <span className={`ml-4 text-xs px-1.5 py-0.5 rounded font-medium ${getTriggerColorClasses(projectWithStage.trigger)}`}>
                              {projectWithStage.stage}
                            </span>
                          )}
                          <BookmarkRemove fontSize="small" color="inherit" className="text-gold ml-auto mt-0.5" />
                        </button>
                        <MobileTooltip title="View" placement="right-start">
                          <Link href={`/projects/${toSlug(project.publicId, project.name)}`} target="_blank" rel="noopener noreferrer">
                            <button
                              type="button"
                              className="flex py-2 align-top cursor-pointer px-2 hover:bg-gray-100 rounded"
                            >
                              <OpenInNew fontSize="small" color="primary" />
                            </button>
                          </Link>
                        </MobileTooltip>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {(!!filteredAvailableProjects.length || !!filteredAvailableLists.length) && (
              <Divider className="my-4" />
            )}

            {/* Favorites List */}
            {favoritesList && (
              <div className="mb-4">
                <div className="px-3 py-1 text-sm font-semibold text-gray-500 mb-2">FAVORITES</div>
                <button
                  type="button"
                  disabled={isSendingData}
                  onClick={() => saveToList(
                    favoritesList,
                    listsWithStages?.some((list) => list.id === favoritesList.id) || false,
                  )}
                  className="flex items-start justify-start p-2 hover:bg-gray-100 cursor-pointer w-full rounded"
                >
                  <div className="flex items-start">
                    {listsWithStages?.some((list) => list.id === favoritesList.id) ? (
                      <CheckIcon color="success" className="mr-2 mt-0.5" />
                    ) : (
                      <AddIcon className="mr-2 mt-0.5" />
                    )}
                    <span className="text-left">{favoritesList.name}</span>
                  </div>
                  {listsWithStages?.some((list) => list.id === favoritesList.id) && (
                    <BookmarkRemove fontSize="small" color="inherit" className="text-gold ml-auto mt-0.5" />
                  )}
                </button>
                <MobileTooltip title="View" placement="right-start">
                  <Link href={`/creators?list=${toSlug(favoritesList.name, favoritesList.publicId)}`} target="_blank" rel="noopener noreferrer">
                    <button
                      type="button"
                      className="flex py-2 align-top cursor-pointer px-2 hover:bg-gray-100 rounded"
                    >
                      <OpenInNew fontSize="small" color="primary" />
                    </button>
                  </Link>
                </MobileTooltip>
              </div>
            )}

            {/* Available Lists Section */}
            {filteredAvailableLists.length > 0 && (
              <div className="mb-4">
                <div className="px-3 py-1 text-sm font-semibold text-gray-500 mb-2">AVAILABLE LISTS</div>
                <div className="space-y-1">
                  {filteredAvailableLists.map((list) => (
                    <div className="flex">
                      <button
                        key={list.id}
                        type="button"
                        disabled={isSendingData}
                        onClick={() => saveToList(list, false)}
                        className="flex items-start justify-start p-2 hover:bg-gray-100 cursor-pointer w-full rounded"
                      >
                        <div className="flex items-start">
                          <AddIcon className="mr-2 mt-0.5" />
                          <div className="flex flex-col">
                            <span className="text-left">{list.name}</span>
                            {list.team?.name && (
                              <span className="text-left text-sm text-gray-500">{list.team.name}</span>
                            )}
                          </div>
                        </div>
                      </button>
                      <MobileTooltip title="View" placement="right-start">
                        <Link href={`/creators?list=${toSlug(list.name, list.publicId)}`} target="_blank" rel="noopener noreferrer">
                          <button
                            type="button"
                            className="flex py-2 align-top cursor-pointer px-2 hover:bg-gray-100 rounded"
                          >
                            <OpenInNew fontSize="small" color="primary" />
                          </button>
                        </Link>
                      </MobileTooltip>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Available Projects Section */}
            {filteredAvailableProjects.length > 0 && (
              <div className="mb-4">
                <div className="px-3 py-1 text-sm font-semibold text-gray-500 mb-2">AVAILABLE PROJECTS</div>
                <div className="space-y-1">
                  {filteredAvailableProjects.map((project) => (
                    <div className="flex">
                      <button
                        key={project.id}
                        type="button"
                        disabled={isSendingData}
                        onClick={() => saveToProject(project, false)}
                        className="flex items-start justify-start p-2 hover:bg-gray-100 cursor-pointer w-full rounded"
                      >
                        <div className="flex items-start">
                          <AddIcon className="mr-2 mt-0.5" />
                          <div className="flex flex-col">
                            <span className="text-left">{project.name}</span>
                            {project.team?.name && (
                              <span className="text-left text-sm text-gray-500">{project.team.name}</span>
                            )}
                          </div>
                        </div>
                      </button>
                      <MobileTooltip title="View" placement="right-start">
                        <Link href={`/projects/${toSlug(project.publicId, project.name)}`} target="_blank" rel="noopener noreferrer">
                          <button
                            type="button"
                            className="flex py-2 align-top cursor-pointer px-2 hover:bg-gray-100 rounded"
                          >
                            <OpenInNew fontSize="small" color="primary" />
                          </button>
                        </Link>
                      </MobileTooltip>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No Results */}
            {searchQuery
              && filteredAlreadySavedLists.length === 0
              && filteredAvailableLists.length === 0
              && filteredAlreadySavedProjects.length === 0
              && filteredAvailableProjects.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No lists or projects found matching &quot;
                {searchQuery}
                &quot;
              </div>
            )}

            {/* Bottom Actions */}
            <Divider className="my-4" />
            <div className="space-y-2">
              <button
                type="button"
                className="py-2 px-3 w-full hover:bg-hintgray flex items-center justify-start rounded"
                onClick={openAddList}
              >
                <AddIcon className="mr-2 mt-0.5" />
                Create a New List
              </button>
              <button
                type="button"
                className="py-2 px-3 w-full hover:bg-hintgray flex items-center justify-start rounded"
                onClick={openAddProject}
              >
                <AddIcon className="mr-2 mt-0.5" />
                Create a New Project
              </button>
              <button
                type="button"
                className="py-2 px-3 w-full hover:bg-hintgray flex items-center justify-start rounded"
                onClick={() => router.push('/lists')}
              >
                Manage Lists
              </button>
              <button
                type="button"
                className="py-2 px-3 w-full hover:bg-hintgray flex items-center justify-start rounded"
                onClick={() => router.push('/projects')}
              >
                Manage Projects
              </button>
            </div>
          </>
        ) : (
          <AddEditList lists={lists || []} onSave={addList} onCancel={() => setAdding(false)} />
        )}
      </div>
    </Dialog>
  );
};
